import {
  impliedPlural,
  STOREFRONT_TERM_KEYS,
  STOREFRONT_TERMS,
  type StorefrontTerminologyOverrides,
  type StorefrontTermKey,
} from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useStorefrontConfigQuery } from "../lib/storefront";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

/** The form's fields as strings; a blank singular is a term left at its default. */
type Draft = Record<StorefrontTermKey, { singular: string; plural: string }>;

function toDraft(overrides: StorefrontTerminologyOverrides): Draft {
  return Object.fromEntries(
    STOREFRONT_TERM_KEYS.map((key) => [
      key,
      { singular: overrides[key]?.singular ?? "", plural: overrides[key]?.plural ?? "" },
    ]),
  ) as Draft;
}

/** The draft as the mutation takes it: trimmed, and only the terms given a singular. */
function toOverrides(draft: Draft): StorefrontTerminologyOverrides {
  const overrides: StorefrontTerminologyOverrides = {};
  for (const key of STOREFRONT_TERM_KEYS) {
    const singular = draft[key].singular.trim();
    const plural = draft[key].plural.trim();
    if (singular) overrides[key] = plural ? { singular, plural } : { singular };
  }
  return overrides;
}

/** Compared per term: the saved value comes back from jsonb, which does not keep key order. */
function isSame(a: StorefrontTerminologyOverrides, b: StorefrontTerminologyOverrides): boolean {
  return STOREFRONT_TERM_KEYS.every(
    (key) =>
      (a[key]?.singular ?? "") === (b[key]?.singular ?? "") &&
      (a[key]?.plural ?? "") === (b[key]?.plural ?? ""),
  );
}

/**
 * The Terminology tab of the Storefront page: what this storefront calls its boxes, items,
 * inventory and battles; blank means the default. Wording, not a feature, so it is a tab rather
 * than a section of its own in the sidebar.
 */
export function StorefrontTerminologyEditor() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { can } = useTenantRole();
  const { queryOptions, query } = useStorefrontConfigQuery();

  const save = useMutation(
    trpc.storefront.setTerminology.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
        toast.success("Terminology saved.");
      },
    }),
  );

  // Narrowing only: the layout renders the page once the same query has data.
  if (!query.data) return null;
  const { config } = query.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Terminology</CardTitle>
        <CardDescription>
          The words this storefront uses for its boxes, items, inventory and battles. Write each as
          it reads mid-sentence, “pack” rather than “Pack”; the storefront capitalizes it where a
          title needs it. Leave a term blank to use the default.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <TerminologyForm
          key={JSON.stringify(config.terminology)}
          overrides={config.terminology}
          canEdit={can("editor")}
          saving={save.isPending}
          onSave={(terminology) => save.mutate({ terminology })}
        />
      </CardContent>
    </Card>
  );
}

/** A singular and a plural per term; remount with `key` when the saved value changes. */
function TerminologyForm({
  overrides,
  canEdit,
  saving,
  onSave,
}: {
  overrides: StorefrontTerminologyOverrides;
  canEdit: boolean;
  saving: boolean;
  onSave: (terminology: StorefrontTerminologyOverrides) => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(overrides));
  const clean = toOverrides(draft);
  const dirty = !isSame(clean, overrides);

  const set = (key: StorefrontTermKey, field: "singular" | "plural", value: string) =>
    setDraft({ ...draft, [key]: { ...draft[key], [field]: value } });

  return (
    <form
      className="grid gap-8"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(clean);
      }}
    >
      {STOREFRONT_TERM_KEYS.map((key) => {
        const term = STOREFRONT_TERMS[key];
        const singular = draft[key].singular.trim();
        return (
          <fieldset key={key} className="grid gap-3">
            {/* A legend sits outside the fieldset's grid, so the gap never reaches it: space it itself. */}
            <legend className="mb-1 text-base font-semibold">{term.label}</legend>
            <p className="text-base text-muted-foreground">{term.hint}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor={`term-${key}-singular`}>Singular</Label>
                <Input
                  id={`term-${key}-singular`}
                  value={draft[key].singular}
                  maxLength={40}
                  disabled={!canEdit}
                  onChange={(e) => set(key, "singular", e.target.value)}
                  placeholder={term.default.singular}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor={`term-${key}-plural`}>Plural</Label>
                {/* Only with a singular: an unset term takes its default plural along with it. */}
                <Input
                  id={`term-${key}-plural`}
                  value={singular ? draft[key].plural : ""}
                  maxLength={40}
                  disabled={!canEdit || !singular}
                  onChange={(e) => set(key, "plural", e.target.value)}
                  placeholder={impliedPlural(key, singular)}
                />
              </div>
            </div>
          </fieldset>
        );
      })}
      {canEdit && (
        <div>
          <Button type="submit" disabled={saving || !dirty}>
            {saving ? "Saving…" : "Save terminology"}
          </Button>
        </div>
      )}
    </form>
  );
}
